import React, { useEffect } from 'react';
import CustomMasonry from 'js/wrapper/CustomMasonry';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { dispatchResizeEvent } from 'helpers/window';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


function CmsElementGallery(props) {
  const { editMode, index, showElementSettings, children } = props;

  // CMS Logic via Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses,
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
    disabled: !editMode,
  });

  /**
   * Effect to handle layout recalculations (Replacing componentDidUpdate)
   * We trigger a resize event when children length changes or settings change.
   * This is crucial for Masonry to recalculate item positions.
   */
  const isCondensed = getParamValue('isCondensed', 'no');
  const childrenLength = children ? React.Children.count(children) : 0;

  useEffect(() => {
    dispatchResizeEvent();

    // Optional: If Masonry needs a tiny delay to see DOM changes
    const timer = setTimeout(() => dispatchResizeEvent(), 100);
    return () => clearTimeout(timer);
  }, [childrenLength, isCondensed]);

  // dnd-kit Styles
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    width: '100%',
    cursor: editMode ? 'move' : 'default',
  };

  // UI Constants
  const shownClass = shown ? 'elem-shown ' : 'elem-hidden ';
  const condensedClass = isCondensed === 'yes' ? ' condensed' : '';

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-gallery-container ${shownClass}${condensedClass} ${cssClasses} ${responsiveClasses}`}
      onClick={(e) => editMode && showElementSettings(e, index)}
      style={getBackgroundStyle()}
    >
      {/* Dynamic CSS styles */}
      {elementStyleTag}

      {children ? (
        <CustomMasonry
          className="ms-gallery"
          columns={{ default: 3, 1200: 3, 768: 2, 480: 1 }}
          gap="15px"
        >
          {children}
        </CustomMasonry>
      ) : (
        <div className="p-t-15 p-b-15 text-center">
          <i className="fa fa-images fa-4x" />
        </div>
      )}

      {getAppendHtml()}
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div ref={setNodeRef} style={dndStyle} {...attributes} {...listeners}>
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
}

export default CmsElementGallery;

